import { useCallback, useMemo } from 'react';

import {
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from 'design-system';
import { XIcon } from 'lucide-react';
import { useSearchParams } from 'react-router';

import FactsheetWindow from '@/features/factsheet/components/FactsheetView/FactsheetWindow';
import { FACTSHEET_TAB_PARAM_NAME } from '@/features/factsheet/constants';
import { removeAtIndex } from '@/features/factsheet/state/factsheetTabsReducer';
import { useFactsheetTabsState } from '@/features/factsheet/state/useFactsheetTabsState';
import { useIsFactsheetCompact } from '@/features/factsheet/state/useIsFactsheetCompact';
import { cn } from '@/lib/utils';
import { addStringIfNotExists } from '@/shared/lib/array/addStringIfNotExists';
import { PageInformationContext } from '@/shared/lib/PageInformationContext';

// Above this many open tabs, finding and closing each one individually gets
// tedious in the catalog's narrow panel, so a single "close all" action is
// offered instead. Only relevant in compact (catalog) mode.
const CLOSE_ALL_TABS_THRESHOLD = 4;

const FactsheetHeaderAction = ({ onDialogClose }: { onDialogClose?: () => void }) => {
  if (!onDialogClose) {
    return (
      <a
        href='/'
        className='flex'
      >
        <img
          alt='logoGx'
          src='/gx-logo-white.svg'
          className='h-10 w-[130px]'
        />
      </a>
    );
  }

  return (
    <Button
      aria-label='close'
      variant='ghost'
      size='icon-sm'
      onClick={event => {
        event.stopPropagation();
        onDialogClose();
      }}
    >
      <XIcon />
    </Button>
  );
};

const FactsheetView = ({
  factsheetKeys,
  onDialogClose,
  setAdditionalTabsParam,
  onOpenTabsChange
}: {
  factsheetKeys: string;
  onDialogClose?: () => void;
  setAdditionalTabsParam?: (tabs: string[]) => void;
  /** Called with the full ordered list of open codes (index 0 is the main one) whenever the set of open tabs changes, so the main tab can be replaced too (e.g. when it's the one being closed). */
  onOpenTabsChange?: (tabs: string[]) => void;
}) => {
  const isCompact = useIsFactsheetCompact();

  const [{ activeTab, openedFactsheets, tabNames }, dispatch] =
    useFactsheetTabsState(factsheetKeys);
  const [, setUrlSearchParams] = useSearchParams();

  // Functional updater: operates on the live search params instead of a
  // snapshot captured at render time, so it doesn't clobber an
  // additional-tabs/main-code update made earlier in the same handler (e.g.
  // by onOpenTabsChange) with a stale copy.
  const deleteActiveTabParam = useCallback(() => {
    setUrlSearchParams(prev => {
      prev.delete(FACTSHEET_TAB_PARAM_NAME);
      return prev;
    });
  }, [setUrlSearchParams]);

  const handleChangeTab = useCallback(
    (code: string) => {
      const index = openedFactsheets.indexOf(code);
      if (index === -1) return;
      deleteActiveTabParam();
      dispatch({ type: 'changeTab', index });
    },
    [deleteActiveTabParam, dispatch, openedFactsheets]
  );

  const onClose = useCallback(
    (indexToClose: number) => {
      // Closing the last remaining tab closes the whole panel instead of
      // leaving an empty header behind.
      if (openedFactsheets.length === 1) {
        onDialogClose?.();
        return;
      }

      const updated = removeAtIndex(openedFactsheets, indexToClose);
      setAdditionalTabsParam?.(updated.slice(1));
      dispatch({ type: 'closeTab', index: indexToClose });
      // onOpenTabsChange already clears the active-tab param as part of its
      // own (transition-wrapped) URL update. Firing deleteActiveTabParam's
      // separate, non-transition update in the same handler would race it and
      // can silently discard the transition's update, so only call it when
      // onOpenTabsChange isn't handling that already.
      if (onOpenTabsChange) {
        onOpenTabsChange(updated);
      } else {
        deleteActiveTabParam();
      }
    },
    [
      deleteActiveTabParam,
      dispatch,
      onDialogClose,
      onOpenTabsChange,
      openedFactsheets,
      setAdditionalTabsParam
    ]
  );

  const handleUpdateTabName = useCallback(
    (code: string, name: string) => {
      dispatch({ type: 'renameTab', code, name });
    },
    [dispatch]
  );

  const handleOpenNewTab = useCallback(
    (code: string) => {
      const isNewTab = !openedFactsheets.includes(code);
      if (isNewTab) {
        const updated = addStringIfNotExists(openedFactsheets, code);
        if (updated.length > 1) {
          setAdditionalTabsParam?.(updated.slice(1));
        }
        onOpenTabsChange?.(updated);
      }
      dispatch({ type: 'openTab', code });
    },
    [dispatch, onOpenTabsChange, openedFactsheets, setAdditionalTabsParam]
  );

  const handleCloseAll = useCallback(() => {
    onDialogClose?.();
  }, [onDialogClose]);

  const pageInformation = useMemo(
    () => ({ mainPageTitle: 'factsheets', subPageTitle: openedFactsheets[activeTab] ?? '' }),
    [openedFactsheets, activeTab]
  );

  const activeCode = openedFactsheets[activeTab];

  return (
    <PageInformationContext value={pageInformation}>
      <div
        className={cn(
          'flex max-w-full flex-col bg-popover',
          isCompact ? 'h-full w-full' : 'mx-16 my-10 w-[1024px]'
        )}
      >
        <Tabs
          className='flex flex-1 flex-col'
          value={activeCode}
          onValueChange={handleChangeTab}
        >
          <div className={cn('flex items-center justify-between', isCompact ? 'p-0' : 'px-6 py-3')}>
            <TabsList>
              {openedFactsheets.map((code, index) => (
                <TabsTrigger
                  key={code}
                  value={code}
                  onClose={
                    isCompact || !!index
                      ? () => {
                          onClose(index);
                        }
                      : undefined
                  }
                >
                  {tabNames.get(code) ?? code}
                </TabsTrigger>
              ))}
            </TabsList>
            {!isCompact && <FactsheetHeaderAction onDialogClose={onDialogClose} />}
            {isCompact && openedFactsheets.length > CLOSE_ALL_TABS_THRESHOLD && (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      aria-label='Close all'
                      variant='ghost'
                      size='icon-sm'
                      className='h-9 w-8'
                      onClick={handleCloseAll}
                    />
                  }
                >
                  <XIcon />
                </TooltipTrigger>
                <TooltipContent>Close all</TooltipContent>
              </Tooltip>
            )}
          </div>

          {openedFactsheets.map(code => (
            <TabsContent
              key={code}
              className='h-full w-full flex-1 flex-grow overflow-hidden'
              value={code}
            >
              <FactsheetWindow
                factsheetKey={code}
                openNewTab={handleOpenNewTab}
                updateTabName={handleUpdateTabName}
              />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </PageInformationContext>
  );
};
export default FactsheetView;
